import { useEffect, useState } from "react";
import MarkdownEditor from "@/components/MarkdownEditor";
import ProfileHeaderBar from "@/components/ProfileHeaderBar";
import { api } from "@/services/api";
import { useToast } from "@/context/ToastContext";
import type { UserProfile } from "@/types";

type TabId = "experience" | "preferences" | "cv_template" | "cl_template";

const TABS: { id: TabId; label: string }[] = [
  { id: "experience",   label: "Experience Vault" },
  { id: "preferences",  label: "Preferences" },
  { id: "cv_template",  label: "Resume Template" },
  { id: "cl_template",  label: "Cover Letter Template" },
];

const EMPTY_PROFILE: UserProfile = {
  experience_data: "",
  preferences: "",
  cv_base_template: "",
  cl_base_template: "",
};

export default function ProfilePage() {
  const [profile, setProfile] = useState<UserProfile>(EMPTY_PROFILE);
  const [savedProfile, setSavedProfile] = useState<UserProfile>(EMPTY_PROFILE);
  const { show: showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("experience");

  const isDirty = JSON.stringify(profile) !== JSON.stringify(savedProfile);

  useEffect(() => {
    api
      .get<UserProfile>("/api/profile")
      .then((data) => {
        setProfile(data);
        setSavedProfile(data);
      })
      .catch((err) => showToast("error", err.detail || "Failed to load profile"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const updated = await api.put<UserProfile>("/api/profile", profile);
      setProfile(updated);
      setSavedProfile(updated);
      showToast("success", "Profile saved.");
    } catch (err: any) {
      showToast("error", err.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="p-6 text-slate-400">Loading profile…</p>;

  return (
    <div className="flex flex-col h-full overflow-hidden max-w-5xl mx-auto w-full">
      {/* Header bar */}
      <ProfileHeaderBar />

      {/* Tab navigation */}
      <div
        role="tablist"
        className="flex flex-shrink-0 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={[
              "px-4 py-3 text-sm whitespace-nowrap transition-colors duration-150 -mb-px",
              activeTab === tab.id
                ? "font-bold text-slate-900 dark:text-slate-100 border-b-2 border-slate-900 dark:border-slate-100"
                : "font-normal text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 border-b-2 border-transparent",
            ].join(" ")}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Action panel */}
      <div
        className="flex flex-shrink-0 items-center gap-2 px-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 h-11"
        aria-label="Tab actions"
      >
        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
        >
          {saving ? "Saving…" : "Save profile"}
        </button>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto">
          <div
            id="tabpanel-experience"
            role="tabpanel"
            hidden={activeTab !== "experience"}
          >
            <MarkdownEditor
              label="Experience Vault"
              value={profile.experience_data}
              onChange={(v) => setProfile((p) => ({ ...p, experience_data: v }))}
              placeholder="Paste your full professional history, education, skills and projects here…"
            />
          </div>
          <div
            id="tabpanel-preferences"
            role="tabpanel"
            hidden={activeTab !== "preferences"}
          >
            <MarkdownEditor
              label="Preferences"
              value={profile.preferences}
              onChange={(v) => setProfile((p) => ({ ...p, preferences: v }))}
              placeholder="Describe your target roles, salary expectations, culture preferences, location…"
            />
          </div>
          <div
            id="tabpanel-cv_template"
            role="tabpanel"
            hidden={activeTab !== "cv_template"}
          >
            <MarkdownEditor
              label="CV Base Template"
              value={profile.cv_base_template}
              onChange={(v) => setProfile((p) => ({ ...p, cv_base_template: v }))}
              placeholder="Define your CV structure with Markdown headings and {{placeholders}}…"
            />
          </div>
          <div
            id="tabpanel-cl_template"
            role="tabpanel"
            hidden={activeTab !== "cl_template"}
          >
            <MarkdownEditor
              label="Cover Letter Base Template"
              value={profile.cl_base_template}
              onChange={(v) => setProfile((p) => ({ ...p, cl_base_template: v }))}
              placeholder="Define your cover letter structure…"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
